<script setup lang="ts">
import { SmallBusinessPlan } from '@/application/smallBusiness/domain/SmallBusinessPlan';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { formatHelpers } from '@hfc/ui-core';

interface emitInterface {
  (event: 'show-benefits', plan: SmallBusinessPlan): void,
  (event: 'show-price-breakdown', plan: SmallBusinessPlan): void,
  (event: 'remove-plan', plan: SmallBusinessPlan): void,
}

const emits = defineEmits<emitInterface>();

const { formatPrice } = formatHelpers();

withDefaults(defineProps<{
  carrierCodeName: string;
  plans: Array<SmallBusinessPlan>;
  sizeWidth?: string;
  titlesClass?: string;
  rateClass?: string;
  optionsClass?: string;
  showIconsOnOptions?: boolean;
  disableRemove?: boolean;
  showPriceBreakdownButton?: boolean;
  detailsButtonName?: string;
  priceBreakdownButtonName?: string;
}>(), {
  sizeWidth: 'max-w-xs',
  titlesClass: 'text-lg font-semibold text-blue-700',
  rateClass: 'text-xl text-red-600',
  optionsClass: 'text-sm text-blue-600',
  showIconsOnOptions: true,
  showPriceBreakdownButton: true,
  detailsButtonName: 'View details',
  priceBreakdownButtonName: 'Pricing Details',
});

const showBenefits = (plan: SmallBusinessPlan): void => {
  emits('show-benefits', plan);
};
const showPriceBreakdown = (plan: SmallBusinessPlan): void => {
  emits('show-price-breakdown', plan);
};
const removePlan = (plan: SmallBusinessPlan): void => {
  emits('remove-plan', plan);
};
</script>
<template>
  <div class="flex flex-col w-full bg-white rounded-2xl px-2 pt-2 border-[3px] border-blue-400" :class="sizeWidth">
    <div class="w-full">
      <carrier-logo custom-class="max-w-full" :carrier-id="carrierCodeName" :vertical="true" :coverage-type="APPLICATION_TYPES.SMALL_BUSINESS" no-link="true" />
    </div>
    <div class="w-full space-y-2 my-2">
      <div v-for="plan in plans" :key="plan.reference_id">
        <div class="flex flex-row items-end">
          <div :class="titlesClass">
            {{plan.name}}
            <!-- Here we should review if we need twoLineName -->
          </div>
        </div>
        <div class="flex w-full flex-row">
          <div class="text-lg">
            Total Price:
          </div>
        </div>
        <div class="flex w-full flex-row">
          <div :class="rateClass">
            {{formatPrice(plan.rate)}}/mo
          </div>
        </div>
        <div class="flex flex-row space-x-3 justify-end">
          <div>
            <button v-if="showIconsOnOptions" v-tooltip="detailsButtonName" class="flex justify-center items-center rounded-xl w-6 h-6 text-blue-500 hover:bg-blue-100" @click="showBenefits(plan)">
              <hfc-icon class="text-sm" icon="eye"></hfc-icon>
            </button>
            <span v-else class="cursor-pointer" :class="optionsClass" @click="showBenefits(plan)">{{ detailsButtonName }}</span>
          </div>
          <div v-if="showPriceBreakdownButton">
            <button v-if="showIconsOnOptions" v-tooltip="priceBreakdownButtonName" class="flex justify-center items-center rounded-xl w-6 h-6 text-green-600 hover:bg-green-100" @click="showPriceBreakdown(plan)">
              <hfc-icon class="text-sm" icon="file-invoice-dollar"></hfc-icon>
            </button>
            <span v-else class="cursor-pointer" :class="optionsClass" @click="showPriceBreakdown(plan)">{{ priceBreakdownButtonName }}</span>
          </div>
          <div v-if="!disableRemove">
            <button v-if="showIconsOnOptions" v-tooltip="'Delete plan'" class="flex justify-center items-center rounded-xl w-6 h-6 text-red-600 hover:bg-red-100" @click="removePlan(plan)">
              <hfc-icon class="text-sm" icon="trash-can"></hfc-icon>
            </button>
            <span v-else class="cursor-pointer" :class="optionsClass" @click="removePlan(plan)">Delete plan</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
